import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner,
    faCheck,
    faTimes,
    faTrash,
    faCoffee,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import { url } from '../../api';
import { apiClient } from '../../api/client';
import { toast } from 'react-toastify';
import './CoffeeSubmissionTracker.css';
import { coffeeRatingPaths } from './api/paths';

interface PendingSubmission {
    id: string;
    coffeeType: string;
    coffeeShop: string;
    status: 'creating' | 'uploading' | 'completed' | 'failed';
    progress: number;
    startTime: Date;
    coffeeId?: string;
    error?: string;
    imageFile?: File;
}

interface CoffeeSubmissionTrackerProps {
    className?: string;
}

const CoffeeSubmissionTracker: React.FC<CoffeeSubmissionTrackerProps> = ({ className = '' }) => {
    const [pendingSubmissions, setPendingSubmissions] = useState<PendingSubmission[]>([]);
    const [isVisible, setIsVisible] = useState(false);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Load pending submissions from localStorage on mount
    useEffect(() => {
        const savedSubmissions = localStorage.getItem('pendingCoffeeSubmissions');
        if (savedSubmissions) {
            const submissions: PendingSubmission[] = JSON.parse(savedSubmissions);
            // Convert date strings back to Date objects
            const submissionsWithDates = submissions.map(sub => ({
                ...sub,
                startTime: new Date(sub.startTime)
            }));
            setPendingSubmissions(submissionsWithDates);
        }
    }, []);

    // Save pending submissions to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('pendingCoffeeSubmissions', JSON.stringify(pendingSubmissions));

        // Set visibility based on whether there are any submissions
        setIsVisible(pendingSubmissions.length > 0);
    }, [pendingSubmissions]);

    // Start polling when there are pending submissions
    useEffect(() => {
        const activePendingSubmissions = pendingSubmissions.filter(
            sub => sub.status === 'creating' || sub.status === 'uploading'
        );

        if (activePendingSubmissions.length > 0 && !pollIntervalRef.current) {
            pollIntervalRef.current = setInterval(pollSubmissions, 3000); // Poll every 3 seconds
        } else if (activePendingSubmissions.length === 0 && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [pendingSubmissions]);

    // Poll server for submission status
    const pollSubmissions = async () => {
        const activePendingSubmissions = pendingSubmissions.filter(
            sub => sub.status === 'creating' || sub.status === 'uploading'
        );

        for (const submission of activePendingSubmissions) {
            try {
                if (submission.coffeeId) {
                    // Check if coffee was created successfully
                    const response = await apiClient.get(`${url}${coffeeRatingPaths.status(submission.coffeeId)}`);

                    if (response.data.status === 'completed') {
                        updateSubmissionStatus(submission.id, 'completed', 100);
                        toast.success(`Coffee "${submission.coffeeType}" was created successfully!`);
                    } else if (response.data.status === 'failed') {
                        updateSubmissionStatus(submission.id, 'failed', 0, response.data.error);
                        toast.error(`Failed to create coffee "${submission.coffeeType}": ${response.data.error}`);
                    }
                }
            } catch (error) {
                console.error('Error polling submission status:', error);
                // Don't update status on network errors - keep polling
            }
        }
    };

    // Add a new submission to track
    const addSubmission = (submission: Omit<PendingSubmission, 'id' | 'startTime'>) => {
        const newSubmission: PendingSubmission = {
            ...submission,
            id: `submission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            startTime: new Date()
        };

        setPendingSubmissions(prev => [...prev, newSubmission]);
        return newSubmission.id;
    };

    // Update submission status
    const updateSubmissionStatus = (
        submissionId: string,
        status: PendingSubmission['status'],
        progress: number,
        error?: string,
        coffeeId?: string
    ) => {
        setPendingSubmissions(prev =>
            prev.map(sub =>
                sub.id === submissionId
                    ? { ...sub, status, progress, error, coffeeId }
                    : sub
            )
        );
    };

    // Remove a submission from tracking
    const removeSubmission = (submissionId: string) => {
        setPendingSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
    };

    // Clear all completed or failed submissions
    const clearCompleted = () => {
        setPendingSubmissions(prev =>
            prev.filter(sub => sub.status === 'creating' || sub.status === 'uploading')
        );
    };

    // Retry a failed submission
    const retrySubmission = async (submissionId: string) => {
        const submission = pendingSubmissions.find(sub => sub.id === submissionId);
        if (!submission) return;

        updateSubmissionStatus(submissionId, 'creating', 10);

        // Here you would trigger the coffee creation process again
        // For now, we'll just simulate it
        setTimeout(() => {
            updateSubmissionStatus(submissionId, 'completed', 100);
        }, 3000);
    };

    // Get status icon
    const getStatusIcon = (status: PendingSubmission['status']) => {
        switch (status) {
            case 'creating':
                return <FontAwesomeIcon icon={faSpinner} spin />;
            case 'uploading':
                return <FontAwesomeIcon icon={faUpload} />;
            case 'completed':
                return <FontAwesomeIcon icon={faCheck} />;
            case 'failed':
                return <FontAwesomeIcon icon={faTimes} />;
            default:
                return <FontAwesomeIcon icon={faSpinner} />;
        }
    };

    // Get status color
    const getStatusColor = (status: PendingSubmission['status']) => {
        switch (status) {
            case 'creating':
            case 'uploading':
                return '#007bff';
            case 'completed':
                return '#28a745';
            case 'failed':
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    // Format elapsed time
    const formatElapsedTime = (startTime: Date) => {
        const elapsed = Date.now() - startTime.getTime();
        const seconds = Math.floor(elapsed / 1000);
        const minutes = Math.floor(seconds / 60);

        if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        }
        return `${seconds}s`;
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className={`coffee-submission-tracker ${className}`}>
            <div className="submission-tracker-header">
                <FontAwesomeIcon icon={faCoffee} />
                <h4>Coffee Submissions</h4>
                <div className="submission-actions">
                    <button
                        className="clear-completed-btn"
                        onClick={clearCompleted}
                        title="Clear completed submissions"
                    >
                        Clear
                    </button>
                </div>
            </div>

            <div className="submissions-list">
                {pendingSubmissions.map(submission => (
                    <div
                        key={submission.id}
                        className={`submission-item ${submission.status}`}
                    >
                        <div className="submission-info">
                            <div className="submission-header">
                                <span
                                    className="submission-status-icon"
                                    style={{ color: getStatusColor(submission.status) }}
                                >
                                    {getStatusIcon(submission.status)}
                                </span>
                                <span className="submission-coffee-type">
                                    {submission.coffeeType}
                                </span>
                                <span className="submission-elapsed-time">
                                    {formatElapsedTime(submission.startTime)}
                                </span>
                            </div>

                            <div className="submission-details">
                                <span className="submission-shop">
                                    {submission.coffeeShop}
                                </span>
                                {submission.status !== 'completed' && submission.status !== 'failed' && (
                                    <div className="submission-progress">
                                        <div
                                            className="progress-bar"
                                            style={{ width: `${submission.progress}%` }}
                                        />
                                    </div>
                                )}
                            </div>

                            {submission.error && (
                                <div className="submission-error">
                                    {submission.error}
                                </div>
                            )}
                        </div>

                        <div className="submission-actions">
                            {submission.status === 'failed' && (
                                <button
                                    className="retry-btn"
                                    onClick={() => retrySubmission(submission.id)}
                                    title="Retry submission"
                                >
                                    <FontAwesomeIcon icon={faSpinner} />
                                </button>
                            )}

                            {(submission.status === 'completed' || submission.status === 'failed') && (
                                <button
                                    className="remove-btn"
                                    onClick={() => removeSubmission(submission.id)}
                                    title="Remove from list"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Export the tracker instance and methods for use in other components
export const coffeeSubmissionTracker = {
    addSubmission: (submission: Omit<PendingSubmission, 'id' | 'startTime'>) => {
        // This is a workaround since we can't directly access the component state
        // In a real app, you'd use a global state management solution like Redux
        const event = new CustomEvent('addCoffeeSubmission', { detail: submission });
        window.dispatchEvent(event);
    },

    updateStatus: (submissionId: string, status: PendingSubmission['status'], progress: number, error?: string, coffeeId?: string) => {
        const event = new CustomEvent('updateCoffeeSubmission', {
            detail: { submissionId, status, progress, error, coffeeId }
        });
        window.dispatchEvent(event);
    }
};

export default CoffeeSubmissionTracker; 